import React from 'react';
import cn from 'classnames';

import Translate from '../Translate';

import styles from './Input.module.scss';

interface SharedInputProps<T> {
  value: T;
  name?: string;
  label?: string | React.ReactNode;
  error?: string | React.ReactNode | boolean | null | undefined;
  hint?: string | React.ReactNode;

  onChange(value: T): void | unknown | Promise<void | unknown>;
}

export interface InputProps<T extends string = string>
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>,
    SharedInputProps<T> {
  iconRight?: React.ReactNode;
  iconLeft?: React.ReactNode;
}

export function TextInput<T extends string = string>(props: InputProps<T>) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { value, onChange, className, label, error, required, pattern, hint, iconLeft, iconRight, ...rest } = props;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as T;
    if (typeof pattern === 'string') {
      const regex = new RegExp(pattern);
      if (!regex.test(value)) {
        return;
      }
    }
    onChange(event.target.value as T);
  };

  return (
    <label
      className={cn(styles.textInput, className, {
        error: !!error,
        disabled: props.disabled,
      })}
    >
      {label && (
        <span className={styles.label}>{typeof label === 'string' ? <Translate id={label} optional /> : label}</span>
      )}
      <div
        className={cn(styles.input, {
          'icon-left': !!iconLeft,
          'icon-right': !!iconRight,
        })}
      >
        {iconLeft && <div className={styles.iconLeft}>{iconLeft}</div>}
        <input type="text" {...rest} onChange={handleChange} value={value} />
        {iconRight && <div className={styles.iconRight}>{iconRight}</div>}
      </div>
      {error && error !== true ? (
        <span className={styles.error}>{typeof error === 'string' ? <Translate id={error} optional /> : error}</span>
      ) : hint ? (
        <span className={styles.hint}>{typeof hint === 'string' ? <Translate id={hint} optional /> : hint}</span>
      ) : null}
    </label>
  );
}

interface TextAreaInputProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'>,
    SharedInputProps<string> {}

export function TextAreaInput(props: TextAreaInputProps) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { value, onChange, className, label, error, required, hint, ...rest } = props;

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(event.target.value);
  };

  return (
    <label
      className={cn(styles.textInput, className, {
        error: !!error,
        disabled: props.disabled,
      })}
    >
      {label && (
        <span className={styles.label}>{typeof label === 'string' ? <Translate id={label} optional /> : label}</span>
      )}
      <div className={styles.input}>
        <textarea {...rest} onChange={handleChange} value={value} />
      </div>
      {error && error !== true ? (
        <span className={styles.error}>{typeof error === 'string' ? <Translate id={error} optional /> : error}</span>
      ) : hint ? (
        <span className={styles.hint}>{typeof hint === 'string' ? <Translate id={hint} optional /> : hint}</span>
      ) : null}
    </label>
  );
}
